import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useSeo } from "../lib/seo";
import Lightbox from "../components/Lightbox";
import FaqSection from "../components/FaqSection";
import PostShare from "../components/PostShare";
import PostRelated from "../components/PostRelated";
import PostSourceCTA from "../components/PostSourceCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level animation configs — hoisted to avoid creating a new object each render.
const bodyMotion = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay: 0.15 },
};
const galleryItemMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

function absoluteUrl(path) {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  if (typeof window === "undefined") return undefined;
  return `${window.location.origin}${path}`;
}

function buildTagLine(tags, kindLabel) {
  const ignored = new Set([kindLabel.toLowerCase(), "news", "essay"]);
  return (tags || [])
    .filter((t) => !ignored.has(t.toLowerCase()))
    .slice(0, 2)
    .join(" · ");
}

function renderInline(text, keyPrefix) {
  // Split on **bold** and *italic* markers; keep the delimiters via capture groups.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    const k = `${keyPrefix}-i-${i}`;
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={k} className="text-white font-medium">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={k}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

function renderBody(content, slug) {
  const blocks = content.split(/\n\n+/);
  return blocks.map((raw, i) => {
    const b = raw.trim();
    const key = `${slug}-b-${i}`;
    if (b.startsWith("### ")) {
      return (
        <h3 key={key} className="font-serif-display text-2xl md:text-3xl text-white mt-10 mb-4">
          {renderInline(b.slice(4), key)}
        </h3>
      );
    }
    if (b.startsWith("## ")) {
      return (
        <h2 key={key} className="font-serif-display text-3xl md:text-4xl text-white mt-12 mb-5 leading-tight">
          {renderInline(b.slice(3), key)}
        </h2>
      );
    }
    if (b.startsWith("- ")) {
      const items = b.split(/\n- /).map((s) => s.replace(/^- /, ""));
      return (
        <ul key={key} className="mt-4 mb-6 space-y-2 pl-0">
          {items.map((item, j) => (
            <li key={`${key}-li-${j}`} className="flex items-start gap-3 text-white/85">
              <span className="text-[var(--accent)] mt-2 w-4 h-px flex-shrink-0" aria-hidden />
              <span className="leading-relaxed">{renderInline(item, `${key}-li-${j}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={key} className={i === 0 ? "drop-cap" : ""}>
        {renderInline(b, key)}
      </p>
    );
  });
}

function buildJsonLd(post, origin) {
  if (!post) return null;
  const absoluteImage = absoluteUrl(post.hero_image);
  const primary = {
    "@context": "https://schema.org",
    "@type": post.category === "news" ? "NewsArticle" : "Article",
    headline: post.title,
    description: post.excerpt,
    image: absoluteImage ? [absoluteImage] : undefined,
    author: { "@type": "Person", name: post.author || "Shaji Nada", url: `${origin}/about` },
    publisher: {
      "@type": "Organization",
      name: "Shaji Nada",
      logo: { "@type": "ImageObject", url: `${origin}/favicon.png` },
    },
    datePublished: post.published_at,
    dateModified: post.published_at,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/journal/${post.slug}` },
    isBasedOn: post.source_url || undefined,
    articleSection: post.category === "news" ? "News" : "Essays",
  };
  if (post.faqs && post.faqs.length > 0) {
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    };
    return [primary, faqPage];
  }
  return primary;
}

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const gallery = post?.gallery || [];
  const isNews = post?.category === "news";
  const kindLabel = isNews ? "News" : "Essay";

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevImage = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + gallery.length) % gallery.length)),
    [gallery.length],
  );
  const nextImage = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % gallery.length)),
    [gallery.length],
  );

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Not found" : e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    axios
      .get(`${API}/posts`)
      .then((r) => {
        const others = (r.data || []).filter((p) => p.slug !== post.slug).slice(0, 3);
        setRelated(others);
      })
      .catch(() => setRelated([]));
  }, [post]);

  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return buildJsonLd(post, origin);
  }, [post]);

  useSeo({
    title: post ? `Shaji Nada | ${post.title}` : "Shaji Nada | Journal",
    description: post?.excerpt || "Journal — essays and news from Shaji Nada.",
    canonicalPath: post ? `/journal/${post.slug}` : "/journal",
    ogImage: absoluteUrl(post?.hero_image),
    jsonLd,
  });

  if (loading) {
    return (
      <div className="pt-40 px-6 md:px-12 text-white/60" data-testid="post-loading">
        Loading…
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="pt-40 px-6 md:px-12 text-white" data-testid="post-error">
        <p className="overline mb-4">404</p>
        <h1 className="font-serif-display text-5xl md:text-6xl">Article not found.</h1>
        <Link to="/journal" className="inline-block mt-8 link-underline text-[var(--accent)] uppercase text-xs tracking-[0.22em]">
          Back to journal →
        </Link>
      </div>
    );
  }

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/journal/${post.slug}`;
  const tagLine = buildTagLine(post.tags, kindLabel);

  return (
    <article data-testid="journal-post-page" className="pt-32 md:pt-40 pb-32">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <nav aria-label="Breadcrumb" data-testid="post-breadcrumb">
          <ol className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/40">
            <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/journal" className="hover:text-white transition-colors">Journal</Link></li>
            <li aria-hidden>/</li>
            <li className="text-[var(--accent)] truncate max-w-[280px]">{post.title}</li>
          </ol>
        </nav>
      </div>

      {/* Header */}
      <header className="mt-8 max-w-[1200px] mx-auto px-6 md:px-12">
        <p className="overline mb-5">{kindLabel}{tagLine ? ` · ${tagLine}` : ""}</p>
        <h1
          className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-white leading-[1.02] max-w-4xl"
          data-testid="post-title"
        >
          {post.title}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.22em] text-white/50">
          <span data-testid="post-author">By {post.author || "Shaji Nada"}</span>
          <span>·</span>
          <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
          <span>·</span>
          <span>{post.read_minutes} min read</span>
          {post.source_name && (
            <>
              <span>·</span>
              <span className="text-[var(--accent)]">Based on {post.source_name}</span>
            </>
          )}
        </div>
      </header>

      {/* Hero image */}
      {post.hero_image && (
        <div className="mt-12 md:mt-16 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="aspect-[16/9] overflow-hidden bg-[#1A1A1A] border border-white/5">
            <img
              src={post.hero_image}
              alt={`${post.title} — Shaji Nada`}
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <motion.div
        {...bodyMotion}
        className="mt-14 max-w-[760px] mx-auto px-6 md:px-0 prose-editorial"
        data-testid="post-content"
      >
        {renderBody(post.content, post.slug)}
      </motion.div>

      {/* Photo Gallery */}
      {gallery.length > 0 && (
        <section
          className="mt-20 md:mt-28 max-w-[1400px] mx-auto px-6 md:px-12"
          data-testid="post-gallery"
          aria-label="Photo gallery"
        >
          <div className="border-t border-white/10 pt-16">
            <p className="overline mb-4">Photo Gallery</p>
            <h2 className="font-serif-display text-3xl md:text-5xl text-white leading-[1.05] mb-12">
              Moments from the ceremony.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((g, i) => (
                <motion.figure
                  key={`${post.slug}-g-${i}`}
                  {...galleryItemMotion}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="group cursor-pointer"
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightboxIdx(i)}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A] border border-white/5 group-hover:border-[var(--accent)]/50 transition-colors duration-500">
                    <img
                      src={g.url}
                      alt={g.alt || g.caption || `${post.title} — photo ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                    />
                  </div>
                  {g.caption && (
                    <figcaption className="mt-3 text-sm text-white/50 font-light leading-relaxed">
                      {g.caption}
                    </figcaption>
                  )}
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <Lightbox
        images={gallery}
        index={lightboxIdx}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
        alt={post.title}
      />

      <FaqSection postSlug={post.slug} faqs={post.faqs} />

      <PostSourceCTA sourceUrl={post.source_url} sourceName={post.source_name} />

      <PostShare title={post.title} shareUrl={shareUrl} />

      <PostRelated related={related} />
    </article>
  );
}
